/**
 * useDocumentSources Hook
 * Standardized API hooks for document source connection operations
 *
 * Refactored: Phase 1 - Factory Pattern
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import type {
  DocumentSourceUserConnectionRequest,
  DocumentSourceProviderConfigRequest,
  DocumentSourceProviderConfigResponse,
  DocumentSourceUserConnectionResponse,
  DocumentSourceSyncStatsResponse,
  OperationResponse,
  ConnectionSyncStatus,
  ProviderConfigDeletionResponse,
} from '../../api/data-contracts';
import { useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIME } from '../../constants';
import { useApiClient } from './useApiClient';
import { createQueryHook, createMutationHook } from './factories';
import { log } from '../../utils/errors/logger';

export interface SharePointFileTreeNode {
  id: string;
  name: string;
  path: string;
  is_file: boolean;
  size?: number;
  modified_at?: string;
  mime_type?: string;
  selected: boolean;
  children: SharePointFileTreeNode[];
}

export interface SharePointBrowseFilesResponse {
  file_tree: SharePointFileTreeNode;
  total_nodes: number;
}

// --- Query Hooks ---

/**
 * Get available document source providers
 */
export const useAvailableProviders = createQueryHook<DocumentSourceProviderConfigResponse[], void>({
  queryKey: () => ['document-sources', 'providers'] as const,
  queryFn: (apiClient) =>
    apiClient.getAvailableProviders().then(response => response.data),
  logContext: 'GET /api/document-sources/providers',
  staleTime: QUERY_STALE_TIME.LONG,
  onSuccess: (data) => {
    log.api.response('GET', '/api/document-sources/providers', 200, {
      dataLength: Array.isArray(data) ? data.length : 'not-array'
    });
  },
});

/**
 * Get user document source connections
 */
export const useUserConnections = createQueryHook<DocumentSourceUserConnectionResponse[], void>({
  queryKey: () => ['document-sources', 'user-connections'] as const,
  queryFn: (apiClient) =>
    apiClient.getUserConnections().then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections',
  staleTime: QUERY_STALE_TIME.SHORT,
  onSuccess: (data) => {
    log.api.response('GET', '/api/document-sources/user/connections', 200, {
      dataLength: Array.isArray(data) ? data.length : 'not-array'
    });
  },
});

/**
 * Get a specific user connection
 */
export const useUserConnection = createQueryHook<DocumentSourceUserConnectionResponse, string>({
  queryKey: (connectionId) => ['document-sources', 'user-connections', connectionId] as const,
  queryFn: (apiClient, connectionId) =>
    apiClient.getUserConnection(connectionId).then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections/:id',
  staleTime: QUERY_STALE_TIME.SHORT,
});

/**
 * Get sync status for a connection
 */
export const useConnectionSyncStatus = createQueryHook<ConnectionSyncStatus, string>({
  queryKey: (connectionId) => ['document-sources', 'user-connections', connectionId, 'sync-status'] as const,
  queryFn: (apiClient, connectionId) =>
    apiClient.getSyncStatus(connectionId).then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections/:id/sync-status',
  staleTime: 0, // Always fetch fresh sync status
});

/**
 * Get connection statistics
 */
export const useConnectionStats = createQueryHook<DocumentSourceSyncStatsResponse, string>({
  queryKey: (connectionId) => ['document-sources', 'user-connections', connectionId, 'stats'] as const,
  queryFn: (apiClient, connectionId) =>
    apiClient.getConnectionStats(connectionId).then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections/:id/stats',
  staleTime: QUERY_STALE_TIME.MEDIUM,
});

/**
 * Get recursive file scan results
 */
export const useRecursiveFileScan = createQueryHook<
  Record<string, unknown>,
  { connectionId: string; max_depth?: number }
>({
  queryKey: ({ connectionId, max_depth }) =>
    ['document-sources', 'user-connections', connectionId, 'recursive-scan', { max_depth }] as const,
  queryFn: (apiClient, { connectionId, max_depth }) =>
    apiClient.recursiveFileScan(
      connectionId,
      { max_depth }
    ).then(response => response.data),
  logContext: 'GET /api/document-sources/user/connections/:id/recursive-scan',
  staleTime: 2 * QUERY_STALE_TIME.MEDIUM, // 2 minutes
});

// --- Mutation Hooks ---

/**
 * Create a new user connection
 */
export const useCreateUserConnection = createMutationHook<
  DocumentSourceUserConnectionResponse,
  DocumentSourceUserConnectionRequest
>({
  mutationFn: (apiClient, connectionData) =>
    apiClient.createUserConnection(connectionData).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'POST /api/document-sources/user/connections',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('connection_created', 'useDocumentSources');
  },
});

/**
 * Update a user connection
 */
export const useUpdateUserConnection = createMutationHook<
  DocumentSourceUserConnectionResponse,
  { connectionId: string; data: DocumentSourceUserConnectionRequest }
>({
  mutationFn: (apiClient, { connectionId, data }) =>
    apiClient.updateUserConnection(connectionId, data).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'PUT /api/document-sources/user/connections/:id',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('connection_updated', 'useDocumentSources', { connectionId: variables.connectionId });
  },
});

/**
 * Delete a user connection
 */
export const useDeleteUserConnection = createMutationHook<OperationResponse, string>({
  mutationFn: (apiClient, connectionId) =>
    apiClient.deleteUserConnection(connectionId).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'DELETE /api/document-sources/user/connections/:id',
  logMethod: 'DELETE',
  onSuccess: (_data, connectionId) => {
    log.user.action('connection_deleted', 'useDocumentSources', { connectionId });
  },
});

/**
 * Browse SharePoint files
 */
export const useBrowseSharePointFiles = createMutationHook<
  SharePointBrowseFilesResponse,
  { connectionId: string; params: { folder_path?: string; max_depth?: number } }
>({
  mutationFn: (apiClient, { connectionId, params }) =>
    apiClient.browseSharepointFiles(
      connectionId,
      params
    ).then(response => response.data),
  logContext: 'POST /api/document-sources/user/connections/:id/browse-files',
  logMethod: 'POST',
});

/**
 * Sync selected files
 */
export const useSyncSelectedFiles = createMutationHook<
  Record<string, unknown>,
  { connectionId: string; params: { selected_tree: unknown; sync_mode: string } }
>({
  mutationFn: (apiClient, { connectionId, params }) =>
    apiClient.syncSelectedFiles(
      connectionId,
      params
    ).then(response => response.data),
  invalidateQueries: [['document-sources', 'user-connections']],
  logContext: 'POST /api/document-sources/user/connections/:id/sync-selected',
  logMethod: 'POST',
  onSuccess: (_data, variables) => {
    log.user.action('selected_files_synced', 'useDocumentSources', { connectionId: variables.connectionId });
  },
});

// ============================================================================
// Admin Provider Configuration Hooks
// ============================================================================

export const documentSourceQueryKeys = {
  all: ['document-sources'] as const,
  providers: () => [...documentSourceQueryKeys.all, 'providers'] as const,
  adminProviderConfigs: () => [...documentSourceQueryKeys.all, 'admin-provider-configs'] as const,
  adminProviderConfigSyncStatus: (configId: string) =>
    [...documentSourceQueryKeys.all, 'admin-provider-configs', configId, 'sync-status'] as const,
  userConnections: () => [...documentSourceQueryKeys.all, 'user-connections'] as const,
};

/**
 * Hook to get all admin-configured document source providers (Admin only).
 * This returns the actual configured provider instances, not just available provider types.
 */
export const useAdminProviderConfigs = createQueryHook<DocumentSourceProviderConfigResponse[], void>({
  queryKey: () => documentSourceQueryKeys.adminProviderConfigs(),
  queryFn: (apiClient) =>
    apiClient.getProviderConfigs().then(response => response.data),
  logContext: 'GET /api/document-sources/admin/provider-configs',
  staleTime: QUERY_STALE_TIME.MEDIUM,
  onSuccess: (data) => {
    log.api.response('GET', '/api/document-sources/admin/provider-configs', 200, {
      dataLength: Array.isArray(data) ? data.length : 'not-array'
    });
  },
});

/**
 * Sync status of every connection under one provider configuration.
 *
 * Loaded whenever the admin page is open so a card can show when the source
 * was last synchronised and how many documents came in, without the user
 * having to trigger a run first. While a run is on its way the caller sets
 * ``live`` and the query repeats every few seconds, so the numbers appear by
 * themselves instead of after a page reload.
 */
export const useProviderConfigSyncStatus = (configId: string | undefined, live = false) => {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: documentSourceQueryKeys.adminProviderConfigSyncStatus(configId ?? ''),
    queryFn: () => apiClient.adminGetProviderConfigSyncStatus(configId as string).then(r => r.data),
    enabled: Boolean(configId),
    staleTime: QUERY_STALE_TIME.SHORT,
    // The server says whether a run is working. While one is, follow it
    // closely; otherwise look in now and then, so a run another administrator
    // started (or a scheduled one) shows up without a reload.
    refetchInterval: query => {
      const running = (query.state.data?.connections ?? []).some(
        c => c.sync_state === 'running' || c.sync_state === 'queued'
      );
      return live || running ? 4000 : 30000;
    },
  });
};

/**
 * Hook to create a new document source provider configuration (Admin only).
 */
export const useCreateProviderConfig = createMutationHook<
  DocumentSourceProviderConfigResponse,
  DocumentSourceProviderConfigRequest
>({
  mutationFn: (apiClient, config) =>
    apiClient.createProviderConfig(config).then(response => response.data),
  invalidateQueries: [
    documentSourceQueryKeys.adminProviderConfigs(),
  ],
  logContext: 'POST /api/document-sources/admin/provider-configs',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('provider_config_created', 'useDocumentSources');
  },
});

/**
 * Hook to update an existing provider configuration (Admin only).
 */
export const useUpdateProviderConfig = createMutationHook<
  DocumentSourceProviderConfigResponse,
  { configId: string; data: DocumentSourceProviderConfigRequest }
>({
  mutationFn: (apiClient, { configId, data }) =>
    apiClient.updateProviderConfig(configId, data).then(response => response.data),
  invalidateQueries: [
    documentSourceQueryKeys.adminProviderConfigs(),
  ],
  logContext: 'PUT /api/document-sources/admin/provider-configs/:configId',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('provider_config_updated', 'useDocumentSources', { configId: variables.configId });
  },
});

/**
 * Hook to trigger a manual full sync for all connections under a provider
 * config (Admin only). Used by the "Sync now" button on the admin
 * DocumentSourceConfig page.
 */
export const useSyncProviderConfig = createMutationHook<
  { message: string; config_id: string; connection_count: number; job_ids: string[] },
  string
>({
  mutationFn: (apiClient, configId) =>
    apiClient
      .adminSyncProviderConfig(configId)
      .then(response => response.data as {
        message: string;
        config_id: string;
        connection_count: number;
        job_ids: string[];
      }),
  invalidateQueries: [
    documentSourceQueryKeys.adminProviderConfigs(),
  ],
  logContext: 'POST /api/document-sources/admin/provider-configs/:configId/sync',
  logMethod: 'POST',
  onSuccess: (_data, configId) => {
    log.user.action('provider_config_sync_triggered', 'useDocumentSources', { configId });
  },
});

/**
 * Hook to delete a provider configuration (Admin only).
 */
export const useDeleteProviderConfig = createMutationHook<ProviderConfigDeletionResponse, string>({
  mutationFn: (apiClient, configId) =>
    apiClient.deleteProviderConfig(configId).then(response => response.data),
  invalidateQueries: [
    documentSourceQueryKeys.adminProviderConfigs(),
  ],
  logContext: 'DELETE /api/document-sources/admin/provider-configs/:configId',
  logMethod: 'DELETE',
  onSuccess: (_data, configId) => {
    log.user.action('provider_config_deleted', 'useDocumentSources', { configId });
  },
});
